import { HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, TimeoutError, timer } from 'rxjs';
import { catchError, retry, timeout } from 'rxjs/operators';

/**
 * Base class for HTTP services that provides common error handling,
 * retry logic, and timeout handling.
 *
 * The regular this.http.<method> call in child services
 * should be wrapped in the applyHttpOperators method to gain access to
 * timeout, retries with delay and exponential backoff as well
 * as error handling.
 */
export abstract class BaseHttpService {
  protected defaultTimeout = 10000;
  protected maxRetries = 2;
  protected retryBaseDelay = 1000;

  /**
   * Applies common HTTP RxJS operators: timeout, retry, and error handling.
   * Can be used in child services for consistent behavior.
   */
  protected applyHttpOperators<T>(source$: Observable<T>): Observable<T> {
    return source$.pipe(
      timeout(this.defaultTimeout),
      this.retryWithDelay(),
      catchError(this.handleError),
    );
  }

  /**
   * Custom retry logic with exponential backoff. Overridable.
   */
  protected retryWithDelay() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (source: Observable<any>) =>
      source.pipe(
        retry({
          count: this.maxRetries,
          delay: (error, retryCount) => {
            if (this.shouldRetry(error)) {
              const delay = this.retryBaseDelay * Math.pow(2, retryCount - 1);
              console.log(
                `Retrying request in ${delay}ms (attempt ${retryCount})`,
              );
              return timer(delay);
            }
            return throwError(() => error);
          },
        }),
      );
  }

  /**
   * Determines if an error should trigger a retry. Overridable.
   */
  protected shouldRetry(
    error: Error | TimeoutError | HttpErrorResponse,
  ): boolean {
    if (error instanceof TimeoutError) return true;
    if (error instanceof HttpErrorResponse) {
      return (
        error.status >= 500 ||
        error.status === 408 ||
        error.status === 429 ||
        error.status === 0
      );
    }
    return false;
  }

  /**
   * Default error handler. Overridable.
   */
  protected handleError(
    error: TimeoutError | HttpErrorResponse,
  ): Observable<never> {
    if (error instanceof TimeoutError) {
      return throwError(() => new Error('Request timed out'));
    }

    // Server-side error
    switch (error.status) {
      case 400:
        return throwError(
          () => new Error('Bad Request: Please check your input'),
        );
      case 401:
        return throwError(() => new Error('Unauthorized: Please log in'));
      case 403:
        return throwError(
          () => new Error('Forbidden: You do not have permission'),
        );
      case 404:
        return throwError(
          () => new Error('Not Found: The requested resource was not found'),
        );
      case 500:
        return throwError(
          () => new Error('Internal Server Error: Please try again later'),
        );
      default:
        return throwError(
          () => new Error(`Server Error: ${error.status} ${error.message}`),
        );
    }
  }
}
